/**
 * A bounded ring buffer of the last N command lifecycle records (web-client.md §8/§9: "last 50
 * cmdId/ack pairs"). It stores only the cmdId (a UUIDv7 – not PII), the wire type token, and the
 * ack phase; NEVER a payload, token, or player-authored text. {@link WsSession} feeds it via an
 * optional observer hook, keeping `net/` free of any diagnostics import. DOM-free.
 */
export type CommandPhase = 'sent' | 'accepted' | 'rejected';

export interface CommandRecord {
  cmdId: string;
  /** Wire type, e.g. `cmd.chat.send`. Safe: no payload. */
  type: string;
  phase: CommandPhase;
  /** Client clock ms at the record (for ordering only). */
  at: number;
}

/** The observer signature WsSession calls; matches {@link CommandLog.record}. */
export type CommandObserver = (record: CommandRecord) => void;

export class CommandLog {
  private readonly ring: CommandRecord[] = [];

  constructor(private readonly capacity = 50) {}

  record(record: CommandRecord): void {
    this.ring.push(record);
    if (this.ring.length > this.capacity) this.ring.shift();
  }

  /** The buffered records, oldest first (a copy – callers may not mutate the ring). */
  entries(): CommandRecord[] {
    return this.ring.slice();
  }

  clear(): void {
    this.ring.length = 0;
  }
}
